import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { z } from 'zod';

const jsonParseTransform = (raw: string, ctx: z.RefinementCtx): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid JSON' });
    return z.NEVER;
  }
};

const stocktakeEntrySchema = z.object({
  expectedStock: z.number().int(),
  countedStock: z.number().int().optional(),
  reason: z.string().optional(),
});

export type StocktakeEntry = z.infer<typeof stocktakeEntrySchema>;

const stocktakeSchema = z.object({ entries: z.record(stocktakeEntrySchema) });

const storedStocktakeSchema = z.string().transform(jsonParseTransform).pipe(stocktakeSchema);

export type Stocktake = z.infer<typeof stocktakeSchema>;

export type StocktakeEntryUpdate = Readonly<{ productId: number } & Partial<StocktakeEntry>>;

export type NewStocktakeEntry = Readonly<{ productId: number; expectedStock: number }>;

// The browser's `storage` event only fires in other tabs, so writes in this tab announce themselves with this one
const STOCKTAKE_CHANGED_EVENT = 'stocktake-changed';

const storageKey = (pharmacyId: number): string => `stocktake/${pharmacyId}`;

const parseStoredStocktake = (raw: string): Stocktake | null => {
  const stored = storedStocktakeSchema.safeParse(raw);
  return stored.success ? stored.data : null;
};

const toStocktakeEntries = (entries: readonly NewStocktakeEntry[]): Stocktake['entries'] =>
  Object.fromEntries(entries.map(({ productId, expectedStock }) => [productId, { expectedStock }]));

const stocktakeStore = {
  getSnapshot(pharmacyId: number): string | null {
    return localStorage.getItem(storageKey(pharmacyId));
  },

  get(pharmacyId: number): Stocktake | null {
    const raw = stocktakeStore.getSnapshot(pharmacyId);
    return raw ? parseStoredStocktake(raw) : null;
  },

  set(pharmacyId: number, stocktake: Stocktake) {
    localStorage.setItem(storageKey(pharmacyId), JSON.stringify(stocktake));
    window.dispatchEvent(new Event(STOCKTAKE_CHANGED_EVENT));
  },

  clear(pharmacyId: number) {
    localStorage.removeItem(storageKey(pharmacyId));
    window.dispatchEvent(new Event(STOCKTAKE_CHANGED_EVENT));
  },

  subscribe(onStoreChange: () => void) {
    window.addEventListener('storage', onStoreChange);
    window.addEventListener(STOCKTAKE_CHANGED_EVENT, onStoreChange);
    return () => {
      window.removeEventListener('storage', onStoreChange);
      window.removeEventListener(STOCKTAKE_CHANGED_EVENT, onStoreChange);
    };
  },
};

type StoredStocktake = {
  stocktake: Stocktake | null;
  startStocktake: (entries: readonly NewStocktakeEntry[]) => void;
  addEntries: (entries: readonly NewStocktakeEntry[]) => void;
  updateEntries: (updates: readonly StocktakeEntryUpdate[]) => void;
  clearStocktake: () => void;
};

/**
 * Keeps a pharmacy's in-progress stocktake in localStorage, in sync across components and browser tabs.
 * @param pharmacyId - The pharmacy whose stocktake to read and write; `null` until it is known, which makes every setter a no-op.
 * @returns The stored stocktake (`null` when none is stored or it is invalid) and setters to start it, add and update entries, and clear it.
 */
export function useStoredStocktake(pharmacyId: number | null): StoredStocktake {
  const raw = useSyncExternalStore(stocktakeStore.subscribe, () =>
    pharmacyId === null ? null : stocktakeStore.getSnapshot(pharmacyId),
  );
  const stocktake = useMemo(() => (raw ? parseStoredStocktake(raw) : null), [raw]);

  const startStocktake = useCallback(
    (entries: readonly NewStocktakeEntry[]) => {
      if (pharmacyId === null) {
        return;
      }
      stocktakeStore.set(pharmacyId, { entries: toStocktakeEntries(entries) });
    },
    [pharmacyId],
  );

  const addEntries = useCallback(
    (entries: readonly NewStocktakeEntry[]) => {
      if (pharmacyId === null) {
        return;
      }
      const current = stocktakeStore.get(pharmacyId);
      if (!current) {
        return;
      }
      const newEntries = entries.filter(({ productId }) => !current.entries[productId]);
      if (newEntries.length === 0) {
        return;
      }
      stocktakeStore.set(pharmacyId, { entries: { ...current.entries, ...toStocktakeEntries(newEntries) } });
    },
    [pharmacyId],
  );

  const updateEntries = useCallback(
    (updates: readonly StocktakeEntryUpdate[]) => {
      if (pharmacyId === null) {
        return;
      }
      const current = stocktakeStore.get(pharmacyId);
      if (!current) {
        return;
      }
      const applicableUpdates = updates.filter(({ productId }) => current.entries[productId]);
      if (applicableUpdates.length === 0) {
        return;
      }
      const entries = { ...current.entries };
      applicableUpdates.forEach(({ productId, ...update }) => {
        entries[productId] = { ...entries[productId], ...update };
      });
      stocktakeStore.set(pharmacyId, { entries });
    },
    [pharmacyId],
  );

  const clearStocktake = useCallback(() => {
    if (pharmacyId === null) {
      return;
    }
    stocktakeStore.clear(pharmacyId);
  }, [pharmacyId]);

  return { stocktake, startStocktake, addEntries, updateEntries, clearStocktake };
}
